/**
 * Helper Functions for Attribute Value Resolution
 *
 * Utilities for finding values from multiple sources, statuses, and activities
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { ActivityContext } from "../types"

/**
 * Find attribute value from multiple statuses (fallback chain)
 *
 * @example
 * // Try Started first, fallback to Scheduled
 * const siteId = findAttributeValue(ctx, "site", [
 *   ActivityEventKind.Started,
 *   ActivityEventKind.Scheduled,
 * ])
 */
export function findAttributeValue(
  ctx: ActivityContext,
  attribute: gqlTypes.ActivityAttributeKind | string,
  statuses: gqlTypes.ActivityEventKind[]
): string | null {
  // Special handling for asset - get from activity.assets, not attributes
  if (attribute === "asset") {
    if (ctx.activity.assets && ctx.activity.assets.length > 0) {
      return ctx.activity.assets[0].id ?? null
    }
    return null
  }

  for (const status of statuses) {
    const attr = ctx.activity.attributes?.find((a) => a.attribute === attribute && a.event_kind === status)

    // Handle different attribute types
    if (attribute === gqlTypes.ActivityAttributeKind.Site) {
      if (attr?.site?.id) return attr.site.id
    } else if (attribute === gqlTypes.ActivityAttributeKind.Batch) {
      if (attr?.batch?.id) return attr.batch.id
    } else if (attr?.value) {
      return attr.value
    }
  }

  return null
}

/**
 * Find attribute value from a different activity in the same order
 *
 * @example
 * // Get batch from the filling activity for use in collection
 * const batchId = findAttributeFromActivity(
 *   ctx,
 *   ActivityKind.Filling,
 *   "batch",
 *   ActivityEventKind.Completed
 * )
 */
export function findAttributeFromActivity(
  ctx: ActivityContext,
  activityKind: gqlTypes.ActivityKind,
  attribute: gqlTypes.ActivityAttributeKind | string,
  status?: gqlTypes.ActivityEventKind
): string | null {
  // Find the specified activity in the order
  const targetActivity = ctx.activities?.find((act) => act.kind === activityKind)

  if (!targetActivity?.attributes) return null

  // If status specified, find from that status only
  if (status) {
    const attr = targetActivity.attributes.find((a) => a.attribute === attribute && a.event_kind === status)
    return extractAttributeValue(attr, attribute)
  }

  // Otherwise, try all statuses in reverse order (Completed → Started → Scheduled)
  const statuses = [
    gqlTypes.ActivityEventKind.Completed,
    gqlTypes.ActivityEventKind.Started,
    gqlTypes.ActivityEventKind.Scheduled,
  ]

  for (const st of statuses) {
    const attr = targetActivity.attributes.find((a) => a.attribute === attribute && a.event_kind === st)
    const value = extractAttributeValue(attr, attribute)
    if (value) return value
  }

  return null
}

/**
 * Extract value from attribute based on type
 */
function extractAttributeValue(attr: any, attribute: gqlTypes.ActivityAttributeKind | string): string | null {
  if (!attr) return null

  if (attribute === gqlTypes.ActivityAttributeKind.Site) {
    return attr.site?.id || null
  } else if (attribute === gqlTypes.ActivityAttributeKind.Batch) {
    return attr.batch?.id || null
  } else {
    return attr.value || null
  }
}

/**
 * Find site value with fallback to order's customer sites
 */
export function findSiteWithFallback(
  ctx: ActivityContext,
  statuses: gqlTypes.ActivityEventKind[],
  siteType?: "filling" | "delivery"
): string | null {
  // Try to find from activity attributes first
  const fromActivity = findAttributeValue(ctx, gqlTypes.ActivityAttributeKind.Site, statuses)
  if (fromActivity) return fromActivity

  // Fallback to order's customer sites
  const siteList = (ctx.order as any).customer_sites || (ctx.order as any).sites || []

  if (siteType) {
    const filteredSites = siteList.filter((cs: any) => {
      const site = cs.site || cs
      return site?.kind === siteType || site?.types?.includes(siteType)
    })
    if (filteredSites.length > 0) {
      const siteData = filteredSites[0].site || filteredSites[0]
      return siteData?.id || null
    }
  }

  // Fallback to any site
  if (siteList.length > 0) {
    const siteData = siteList[0].site || siteList[0]
    return siteData?.id || null
  }

  return null
}

/**
 * Find attribute value from a linked activity (via source_links or target_links)
 *
 * @param ctx - Activity context
 * @param linkType - "source" for delivery activities, "target" for collection activities
 * @param attribute - Attribute to find (e.g., fill_level, quantity)
 * @param status - Event status to look for (e.g., Completed, Started)
 * @returns The attribute value or null if not found
 *
 * @example
 * // Get fill_level from delivery activity's completed status
 * const fillLevel = findAttributeFromLinkedActivity(
 *   ctx,
 *   "source",
 *   ActivityAttributeKind.FillLevel,
 *   ActivityEventKind.Completed
 * )
 *
 * Note: This function requires the linked activities to be included in ctx.activities.
 * If the linked activity is not found, it returns null and the field will fall back
 * to other default values or manual entry.
 */
export function findAttributeFromLinkedActivity(
  ctx: ActivityContext,
  linkType: "source" | "target",
  attribute: gqlTypes.ActivityAttributeKind | string,
  status: gqlTypes.ActivityEventKind | gqlTypes.ActivityEventKind[]
): string | null {
  status = Array.isArray(status) ? status : [status]
  // Get the appropriate links based on type
  const links = linkType === "source" ? ctx.activity.source_links : ctx.activity.target_links

  if (!links || links.length === 0) {
    return null
  }

  // Get the first linked activity (assume single link for now)
  const linkedActivityId = links[0].activity_id

  // Find the linked activity in the activities context
  const linkedActivity = ctx.activities?.find((act) => act.id === linkedActivityId)

  if (!linkedActivity?.attributes) {
    return null
  }

  // Find the attribute with the specified status
  const attrs = linkedActivity.attributes.filter((a) => a.attribute === attribute && status.includes(a.event_kind))
  const last_status = status[status.length - 1]
  const attr = attrs.find((a) => a.event_kind === last_status)

  if (!attr) {
    return null
  }

  return extractAttributeValue(attr, attribute)
}
